import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import React from 'react';
import renderCellMapper from './utils/render-cell-mapper';
import { DEFAULT_TABLE_CLASS_NAME } from '../../theme/constants/table';

const FingoTableMobile = ({
  columns,
  row,
  checkboxSelection,
  isRowSelectable,
  selectionModel,
  onSelectionModelChange,
  collapsible,
  isRowCollapsible,
  collapseComponent: CollapseComponent,
  open,
  toggleOpen,
  tableClassName,
}) => {
  const selectClickHandler = (event, id) => {
    const isChecked = !!event.target?.checked;
    if (isChecked) {
      onSelectionModelChange([...selectionModel, id]);
    } else {
      onSelectionModelChange(selectionModel.filter((rowId) => rowId !== id));
    }
  };

  const collapsibleRow = collapsible && isRowCollapsible({ row });
  const selectableRow = checkboxSelection && isRowSelectable({ row });

  // Group columns by level (default to 0 if not specified)
  const columnsByLevel = columns.reduce((acc, column) => {
    const level = column.level || 0;
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(column);
    return acc;
  }, {});

  const maxLevel = Math.max(...Object.keys(columnsByLevel).map(Number), 0);

  return (
    <>
      <TableRow hover role="checkbox" tabIndex={-1}>
        {(collapsible || checkboxSelection) && (
          <TableCell
            padding="checkbox"
            className={tableClassName}
            sx={{
              minWidth: collapsibleRow && selectableRow ? '70px' : null,
              verticalAlign: 'top',
            }}
          >
            {/* Controls only in the first level */}
            {selectableRow && (
              <Checkbox
                color="primary"
                checked={selectionModel.includes(row.id)}
                indeterminate={!isRowSelectable({ row })}
                disabled={!isRowSelectable({ row })}
                onChange={(event) => selectClickHandler(event, row.id)}
              />
            )}
            {collapsibleRow && (
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => {
                  toggleOpen();
                }}
              >
                {open ? <KeyboardArrowUp sx={{ color: 'primary.main' }} />
                  : <KeyboardArrowDown sx={{ color: 'primary.main' }} />}
              </IconButton>
            )}
          </TableCell>
        )}

        {/* Main content cell with all levels stacked vertically */}
        <TableCell colSpan={columns.length} className={tableClassName} sx={{ p: 0, border: 'none' }}>
          <Stack spacing={0}>
            {Array.from({ length: maxLevel + 1 }, (_, level) => (
              <Box
                key={`level-${level}`}
              >
                <Stack
                  direction={level === 0 ? 'row' : 'column'}
                  alignItems="center"
                  spacing={level === 0 ? 1 : 2}
                >
                  {(columnsByLevel[level] || []).map((column) => (
                    <Box
                      key={`${row.id}-${column.field}`}
                      sx={{
                        minWidth: column.width,
                        width: column.width,
                        py: 1,
                      }}
                    >
                      {column.renderCell
                        ? column.renderCell({ row })
                        : renderCellMapper[column.type](column.valueGetter({ row }))}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </TableCell>
      </TableRow>

      {collapsibleRow && open && (
        <TableRow hover role="checkbox" tabIndex={-1}>
          <TableCell style={{ padding: 0 }} className={tableClassName} colSpan={columns.length + 1}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <CollapseComponent row={row} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

FingoTableMobile.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string,
      sortable: PropTypes.bool,
      renderCell: PropTypes.func,
      valueGetter: PropTypes.func,
      level: PropTypes.number, // Prop for mobile layout level
    }),
  ).isRequired,
  row: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  checkboxSelection: PropTypes.bool.isRequired,
  isRowSelectable: PropTypes.func.isRequired,
  selectionModel: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionModelChange: PropTypes.func.isRequired,
  collapsible: PropTypes.bool.isRequired,
  isRowCollapsible: PropTypes.func.isRequired,
  collapseComponent: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  toggleOpen: PropTypes.func.isRequired,
  tableClassName: PropTypes.string,
};

FingoTableMobile.defaultProps = {
  tableClassName: DEFAULT_TABLE_CLASS_NAME,
};

export default FingoTableMobile;
